// ---------------------------------------------------------------------------
// Felton Chart DNA -- Color Palette Constants
// ---------------------------------------------------------------------------
// 8-color palette array sourced from CSS custom properties defined in
// index.css. Utility functions provide safe index access with modulo wrap.
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ChartColor {
  readonly name: string
  readonly cssVar: string
  readonly tailwind: string
}

// ---------------------------------------------------------------------------
// Palette Array (matches --felton-chart-1 through --felton-chart-8)
// ---------------------------------------------------------------------------

const colors: ReadonlyArray<ChartColor> = [
  { name: 'Indigo', cssVar: 'var(--felton-chart-1)', tailwind: 'felton-chart-1' },
  { name: 'Teal', cssVar: 'var(--felton-chart-2)', tailwind: 'felton-chart-2' },
  { name: 'Amber', cssVar: 'var(--felton-chart-3)', tailwind: 'felton-chart-3' },
  { name: 'Rose', cssVar: 'var(--felton-chart-4)', tailwind: 'felton-chart-4' },
  { name: 'Violet', cssVar: 'var(--felton-chart-5)', tailwind: 'felton-chart-5' },
  { name: 'Emerald', cssVar: 'var(--felton-chart-6)', tailwind: 'felton-chart-6' },
  { name: 'Sky', cssVar: 'var(--felton-chart-7)', tailwind: 'felton-chart-7' },
  { name: 'Slate', cssVar: 'var(--felton-chart-8)', tailwind: 'felton-chart-8' },
] as const

// Freeze each entry and the array itself for true immutability
colors.forEach((c) => Object.freeze(c))
export const CHART_COLORS: ReadonlyArray<ChartColor> = Object.freeze([...colors])

export const CHART_COLOR_COUNT = 8

// ---------------------------------------------------------------------------
// Utility Functions
// ---------------------------------------------------------------------------

/**
 * Returns the ChartColor at the given index, wrapping around via modulo.
 * Handles negative indices safely.
 */
export function getChartColor(index: number): ChartColor {
  const len = CHART_COLORS.length
  const safeIndex = ((index % len) + len) % len
  return CHART_COLORS[safeIndex]
}

/**
 * Returns a CSS `hsl(...)` string for the chart color at the given index.
 * Safe for inline styles and SVG fill/stroke attributes.
 */
export function getChartColorCss(index: number): string {
  const color = getChartColor(index)
  return `hsl(${color.cssVar})`
}
